import React from 'react';
import { Download, FileArchive, CheckCircle2, AlertCircle } from 'lucide-react';

const DownloadPage = () => {
  return (
    <div className="min-h-screen bg-black py-24 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <img 
            src="/milazzo-logo.svg" 
            alt="Milazzo Pizza" 
            className="h-20 w-auto mx-auto mb-6"
          />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Download Website Files
          </h1>
          <p className="text-gray-400 text-lg">
            Ready to upload to your FTP server
          </p>
        </div>

        {/* Download Card */}
        <div className="bg-gradient-to-br from-red-950/30 to-black border border-red-900/30 rounded-lg p-8 mb-8">
          <div className="flex items-start gap-6 mb-6">
            <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center flex-shrink-0">
              <FileArchive className="w-8 h-8 text-red-500" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">milazzo-website-ftp.zip</h2>
              <p className="text-gray-400 mb-4">Complete website package - Ready for FTP upload</p>
              <div className="text-sm text-gray-500 mb-4">Size: ~422 KB</div>
              
              <a
                href="/downloads/milazzo-website-ftp.zip"
                download
                className="inline-flex items-center gap-3 px-8 py-4 bg-red-600 text-white font-medium tracking-wider hover:bg-red-700 transition-all duration-300 transform hover:scale-105"
              >
                <Download className="w-5 h-5" />
                Download Now
              </a>
            </div>
          </div>
        </div>

        {/* What's Included */}
        <div className="bg-gradient-to-br from-red-950/30 to-black border border-red-900/30 rounded-lg p-8 mb-8">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-500" />
            What's Included
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <div className="text-white font-medium">Optimized Files</div>
                <div className="text-gray-400 text-sm">Production-ready HTML, CSS, JS</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <div className="text-white font-medium">Your Logo</div>
                <div className="text-gray-400 text-sm">Custom Milazzo logo included</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <div className="text-white font-medium">3 Languages</div>
                <div className="text-gray-400 text-sm">Finnish, English, Swedish</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <div className="text-white font-medium">.htaccess File</div>
                <div className="text-gray-400 text-sm">Apache configuration included</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <div className="text-white font-medium">Fully Responsive</div>
                <div className="text-gray-400 text-sm">Works on all devices</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <div className="text-white font-medium">README Guide</div>
                <div className="text-gray-400 text-sm">Step-by-step instructions</div>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Instructions */}
        <div className="bg-gradient-to-br from-red-950/30 to-black border border-red-900/30 rounded-lg p-8">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-500" />
            Quick Upload Guide
          </h3>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 text-white font-bold">
                1
              </div>
              <div>
                <div className="text-white font-medium mb-1">Extract the ZIP file</div>
                <div className="text-gray-400 text-sm">Unzip the downloaded file to your computer</div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 text-white font-bold">
                2
              </div>
              <div>
                <div className="text-white font-medium mb-1">Connect via FTP</div>
                <div className="text-gray-400 text-sm">Use FileZilla or your hosting's file manager</div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 text-white font-bold">
                3
              </div>
              <div>
                <div className="text-white font-medium mb-1">Upload to root directory</div>
                <div className="text-gray-400 text-sm">Upload all files to public_html or www folder</div>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 text-white font-bold">
                4
              </div>
              <div>
                <div className="text-white font-medium mb-1">Point your domain</div>
                <div className="text-gray-400 text-sm">Configure milazzo.fi to point to your hosting</div>
              </div>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="text-red-500 hover:text-red-400 transition-colors duration-300"
          >
            ← Back to Website
          </a>
        </div>
      </div>
    </div>
  );
};

export default DownloadPage;
